/* eslint-disable no-param-reassign */
import {
	Broadcast,
	BroadcastInsights,
	BroadcastStatus,
	BroadcastViews,
} from 'types'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import getAllBroadcasts, {
	GetAllBroadcastsParams,
	GetAllBroadcastsResponse,
} from 'api/broadcasts/getAllBroadcasts'
import handleAxiosError from 'utils/handleAxiosError'
import getBroadcastsInsights, {
	GetBroadcastsInsightsParams,
	GetBroadcastsInsightsResponse,
} from 'api/broadcasts/getBroadcastsInsights'
import getBroadcastsView, {
	GetBroadcastsViewResponse,
} from 'api/broadcasts/getBroadcastsView'

export const BROADCASTS_FETCH_SIZE = 20

export interface BroadcastsState {
	records: Array<Broadcast>
	currentPage: number
	totalCount: number
	totalPages: number
	loading: boolean
	query: string
	status: BroadcastStatus
	view: 'LIST' | 'STATS'
	insights: BroadcastInsights | null
	insightsLoading: boolean
	views: BroadcastViews | null
	viewsLoading: boolean
}

export const getAllBroadcastsAction = createAsyncThunk<
	GetAllBroadcastsResponse,
	GetAllBroadcastsParams
>('broadcasts/getAllBroadcastsAction', async (payload, { rejectWithValue }) => {
	try {
		return await getAllBroadcasts(payload)
	} catch (err: any) {
		handleAxiosError(err)
		return rejectWithValue('Failed to fetch broadcasts')
	}
})

export const getBroadcastsInsightsAction = createAsyncThunk<
	GetBroadcastsInsightsResponse,
	GetBroadcastsInsightsParams | void
>(
	'broadcasts/getBroadcastsInsightsAction',
	async (payload, { rejectWithValue }) => {
		try {
			return await getBroadcastsInsights(payload ?? {})
		} catch (error: any) {
			handleAxiosError(error)
			return rejectWithValue('Failed to fetch broadcasts insights')
		}
	}
)

export const getBroadcastViewsAction = createAsyncThunk<
	GetBroadcastsViewResponse,
	string | void
>(
	'broadcasts/getBroadcastViewsAction',
	async (clientId, { rejectWithValue }) => {
		try {
			return await getBroadcastsView({
				client: clientId ?? undefined,
			})
		} catch (error: any) {
			handleAxiosError(error)
			return rejectWithValue('Failed to fetch broadcasts views')
		}
	}
)

const initialState: BroadcastsState = {
	records: [],
	currentPage: 1,
	totalCount: 0,
	totalPages: 0,
	loading: false,
	query: '',
	status: 'ALL' as BroadcastStatus,
	view: 'STATS',
	insights: null,
	insightsLoading: false,
	views: null,
	viewsLoading: false,
}

const broadcastsSlice = createSlice({
	name: 'broadcasts',
	initialState: initialState as BroadcastsState,
	reducers: {
		clearBroadcasts: () => {
			return initialState
		},
		setQuery: (state, action: PayloadAction<string>) => {
			state.query = action.payload
		},
		setStatus: (state, action: PayloadAction<BroadcastStatus>) => {
			state.status = action.payload
		},
		setView: (state, action: PayloadAction<'LIST' | 'STATS'>) => {
			state.view = action.payload
		},
	},
	extraReducers: builder => {
		builder.addCase(getAllBroadcastsAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getAllBroadcastsAction.fulfilled, (state, { payload }) => {
			state.loading = false
			state.records = payload.records ?? []
			state.currentPage = payload.currentPage ?? 1
			state.totalCount = payload.totalCount ?? 0
			state.totalPages = payload.totalPages ?? 0
		})
		builder.addCase(getAllBroadcastsAction.rejected, state => {
			state.loading = false
		})

		builder.addCase(getBroadcastsInsightsAction.pending, state => {
			state.insightsLoading = true
		})
		builder.addCase(
			getBroadcastsInsightsAction.fulfilled,
			(state, { payload }) => {
				state.insightsLoading = false
				state.insights = payload.insights ?? null
			}
		)
		builder.addCase(getBroadcastsInsightsAction.rejected, state => {
			state.insightsLoading = false
		})
		builder.addCase(getBroadcastViewsAction.pending, state => {
			state.viewsLoading = true
		})
		builder.addCase(getBroadcastViewsAction.fulfilled, (state, { payload }) => {
			state.viewsLoading = false
			state.views = payload.reduce((acc, curr) => {
				acc[curr._id.status] = curr.count
				return acc
			}, {} as BroadcastViews)
		})
		builder.addCase(getBroadcastViewsAction.rejected, state => {
			state.viewsLoading = false
		})
	},
})

export const { clearBroadcasts, setQuery, setStatus, setView } =
	broadcastsSlice.actions

export default broadcastsSlice.reducer
